import { i18n } from "../i18n";
import type { AstrologyInput, HouseSection, Report, HarmonicsResponse, TransitsSection, V413Features, LibraryAuthor, LibraryInterpretation, LibraryChartMatch, LibraryCategoryCount, LibraryStats, HeroCaseSummary, HeroCaseDetail, LifeSpiral, MundaneResponse, MundaneTimelineResponse, MundaneCenturySeries, MundaneDiscoveriesResponse, MundaneOnThisDayResponse, MundanePreRegStatusResponse, MundaneTodayResponse, MundaneExplainResponse, MundaneCountriesTodayResponse, MundaneBriefingResponse, MundaneYtdVerdictResponse, MundaneChronosYearResponse, MundaneChronosRuleCatalogResponse, MundaneProfilesIndexResponse, MundaneCountryProfileResponse, MundaneOutcomesSummaryResponse, CelebrityAssessmentResponse, AscendantProfile, PlannerWindowResponse, PlannerScanResponse, PlannerHorizonResponse, PlannerValidationResponse } from "./types";
import type {
  FaceHouseAssessmentResult,
  FaceAspectAssessmentResult,
  FortitudeWithLotsResult,
  PlanetReadingResult,
  PlanetAssessmentResult,
} from "./faceTable";

const PROD_API = "https://api.astrogenesis.net";
const isProdHost =
  typeof window !== "undefined" &&
  /(^|\.)astrogenesis\.net$/.test(window.location.hostname);
const BASE = isProdHost
  ? PROD_API
  : import.meta.env.VITE_API_BASE
    || (import.meta.env.PROD ? PROD_API : "/api");
/** Exported so direct fetch() callers can honour VITE_API_BASE too */
export const apiBase = BASE;

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

// Backend reports normally complete in 5–15 s. Cap the wait so spinners don't
// hang forever when the Railway service flaps — without a timeout the fetch
// can sit indefinitely and the user has no feedback. Chart-request v2 endpoints
// can still spend ~25s on a cold Railway + cache miss, so web matches the iOS
// 60s budget. Mundane cache misses keep their own narrower budget.
const FETCH_TIMEOUT_MS = 35_000;
export const CHART_REQUEST_TIMEOUT_MS = 60_000;
export const MUNDANE_REQUEST_TIMEOUT_MS = 45_000;
const FETCH_BLOB_TIMEOUT_MS = 60_000;
export const DISCOVERIES_EVIDENCE_CONTRACT_VERSION = "v4.13-receipts-2";

function fetchWithTimeout(input: RequestInfo, init: RequestInit, timeoutMs: number): Promise<Response> {
  // AbortSignal.timeout would be cleaner but isn't in every supported browser
  // yet; an explicit controller works everywhere we ship to.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(input, { ...init, signal: controller.signal })
    .catch((err) => {
      if (err?.name === "AbortError") {
        throw new ApiError(0, `Request timed out after ${Math.round(timeoutMs / 1000)}s — the calculation server may be warming up. Please try again.`);
      }
      throw err;
    })
    .finally(() => clearTimeout(timer));
}

/**
 * url() — normalize a path against BASE so dev URLs don't double-prefix.
 *
 * In dev the BASE is "/api" (so the Vite proxy can catch it) but legacy
 * callers used paths like "/report/" and v2 callers use "/api/v2/timeline"
 * — naive concatenation produces "/api/api/v2/timeline" which works through
 * the proxy's rewrite but is ugly. This helper:
 *   - if BASE ends in "/api" AND path starts with "/api/", strip one "/api"
 *   - otherwise, concatenate as-is
 * Result: every URL is either "/api/v2/..." or "https://api.astrogenesis.net/api/v2/..."
 * — never a doubled prefix.
 */
function url(path: string): string {
  if (BASE.endsWith("/api") && path.startsWith("/api/")) {
    return BASE + path.slice(4);
  }
  return BASE + path;
}

function getCacheKey(path: string, body?: unknown): string {
  const bodyStr = body ? JSON.stringify(body) : "";
  return `ag_api_cache:${path}:${bodyStr}`;
}

function saveCache(key: string, data: unknown): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // Quota exceeded or private browsing — fail silently
  }
}

function loadCache<T>(key: string): T | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object" && "data" in parsed ? parsed.data : parsed) as T;
  } catch {
    return null;
  }
}

async function get<T>(path: string, timeoutMs = FETCH_TIMEOUT_MS, extraHeaders?: Record<string, string>): Promise<T> {
  const cacheKey = getCacheKey(path);
  try {
    const res = await fetchWithTimeout(url(path), {
      method: "GET",
      headers: { Accept: "application/json", ...extraHeaders },
    }, timeoutMs);
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
    const data = (await res.json()) as T;
    saveCache(cacheKey, data);
    return data;
  } catch (err) {
    const cached = loadCache<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }
    throw err;
  }
}

async function post<T>(
  path: string,
  body: unknown,
  accept: "json" | "text" = "json",
  timeoutMs = FETCH_TIMEOUT_MS,
): Promise<T> {
  const cacheKey = getCacheKey(path, body);
  try {
    const res = await fetchWithTimeout(url(path), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: accept === "json" ? "application/json" : "text/plain",
      },
      body: JSON.stringify(body),
    }, timeoutMs);
    if (!res.ok) {
      const text = await res.text();
      throw new ApiError(res.status, text || res.statusText);
    }
    const data = accept === "json" ? ((await res.json()) as T) : ((await res.text()) as unknown as T);
    saveCache(cacheKey, data);
    return data;
  } catch (err) {
    const cached = loadCache<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }
    throw err;
  }
}

async function postBlob(path: string, body: unknown): Promise<Blob> {
  const res = await fetchWithTimeout(url(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }, FETCH_BLOB_TIMEOUT_MS);
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || res.statusText);
  }
  return await res.blob();
}

function postChart<T>(path: string, body: unknown, accept: "json" | "text" = "json"): Promise<T> {
  return post<T>(path, body, accept, CHART_REQUEST_TIMEOUT_MS);
}

function getMundane<T>(path: string): Promise<T> {
  // Phase 3: the backend negotiates response prose (?locale= →
  // X-Astrogenesis-Locale → Accept-Language → en). Send the UI locale
  // explicitly so methodology / scope notes / caveats render localized;
  // the backend keys its mundane caches per locale (no cross-talk).
  return get<T>(path, MUNDANE_REQUEST_TIMEOUT_MS, {
    "X-Astrogenesis-Locale": currentUiLocale(),
  });
}

function postMundane<T>(path: string, body: unknown): Promise<T> {
  return post<T>(path, body, "json", MUNDANE_REQUEST_TIMEOUT_MS);
}

/** Active UI locale tag for content negotiation (read per request). */
function currentUiLocale(): string {
  return i18n.language || "en";
}

// ============================================================================
// V4.13 research layer — long-form discovery article shape
// ============================================================================
export interface DiscoveryExtended {
  slug: string;
  title: string;
  finding: string;
  implication: string;
  stance: string;
  methodology: {
    corpus_size: number;
    rodden_filter: string;
    statistical_test: string;
    baseline_reference: string;
    null_hypothesis: string;
    multiple_comparison_correction: string;
    inclusion_criteria: string[];
    exclusion_criteria: string[];
    confusion_matrix: Record<string, unknown>;
    raw_data_url?: string;
    classical_shloka?: string;
  };
  stats: Record<string, unknown>;
  example_celebs: string[];
  citations: Array<{ book: string; chapter: string }>;
}

// ============================================================================
// Journey (v2) — unified life-predictions endpoint
// ============================================================================
export interface JourneyNowBar {
  mahadasha: string;
  antardasha: string;
  pratyantardasha: string;
  headline: string;
  mahadasha_start: string;
  mahadasha_end: string;
}

export interface JourneyNext90Item {
  date: string;
  title: string;
  detail: string;
  tier: string;
  tradition_voices: string[];
}

export interface JourneyYearAheadItem {
  event_type: string;
  title: string;
  window: string;
  score: number;
  tier: string;
  rationale: string;
  dasha_signature: string;
}

export interface JourneyTenYearItem {
  year: number;
  lord: string;
  phase: string;
}

export interface JourneyMilestoneItem {
  event_type: string;
  title: string;
  best_window: string;
  dasha_signature: string;
  score: number;
  tier: string;
  rationale: string;
}

export interface JourneyMeta {
  items_per_band: number;
  generated_at_utc: string;
  engine_version: string;
}

export interface JourneyResponse {
  now_bar: JourneyNowBar;
  next_90_days: JourneyNext90Item[];
  year_ahead: JourneyYearAheadItem[];
  ten_year_arc: JourneyTenYearItem[];
  milestones: JourneyMilestoneItem[];
  meta: JourneyMeta;
}

/** R-341 measured top-30 confidence for a mahadasha lord (not classical strength). */
export interface MahadashaConfidence {
  lord: string;
  top30_capture: number;
  corpus_top30_capture: number;
  delta_vs_corpus: number;
  plain_language: string;
  source?: string;
  predictive_strength_score?: boolean;
}

export interface TimelineMahadasha {
  lord: string;
  years: number;
  start_date: string;
  end_date: string;
  start_age: number | null;
  end_age: number | null;
  is_current: boolean;
  interpretation?: string | null;
  situation?: Record<string, unknown>;
  /** Measured engine hit rate for this MD lord (R-341 / R-342). */
  confidence?: MahadashaConfidence | null;
}

export interface TimelineResponse {
  birth: Record<string, unknown>;
  current_age: number;
  timelines: {
    vedic_dasha: {
      system: string;
      stance: string;
      balance_at_birth: Record<string, unknown> | null;
      current: {
        mahadasha: string;
        antardasha: string;
        pratyantardasha?: string;
        date?: string;
      };
      current_situation?: Record<string, unknown>;
      mahadashas: TimelineMahadasha[];
    };
    annual_profection: Record<string, unknown> | null;
    zodiacal_releasing: Record<string, unknown> | null;
  };
  combined: Record<string, unknown>;
}

// ============================================================================
// Life predictions (v2) — ages ledger event chips
// ============================================================================
export interface LifePredictionWindow {
  md?: string;
  ad?: string;
  pd?: string;
  start_date?: string;
  end_date?: string;
  duration_days?: number;
  score?: number;
  rationale?: string;
  valence?: string;
}

export interface LifePredictionEvent {
  description?: string;
  windows: LifePredictionWindow[];
}

export interface LifePredictionsResponse {
  events: Record<string, LifePredictionEvent>;
}

// ============================================================================
// Health timeline (v2) — non-clinical age states
// ============================================================================
export type HealthStateWord = "steady" | "sensitive" | "turning" | "strong";

export interface HealthAge {
  age: number;
  years: string;
  state: HealthStateWord | string;
  summary: string;
  windows?: Array<{
    event?: string;
    start_date?: string | null;
    end_date?: string | null;
    score?: number | null;
    rationale?: string | null;
  }>;
}

export interface HealthTimelineResponse {
  ages: HealthAge[];
}

// ============================================================================
// Horary (v2) — Ask tab / Bonatti engine
// ============================================================================
export interface HoraryResponse {
  question?: string;
  original_question?: string;
  matched_topic?: string;
  category?: string;
  verdict?: string;
  rationale?: string;
  timing_estimate?: string | null;
  querent_significator?: string;
  querent_house?: number;
  querent_dignity?: string;
  quesited_significator?: string;
  quesited_house?: number;
  quesited_dignity?: string;
  aspect_between_significators?: string | null;
  reception?: string | null;
  translation_of_light_via_moon?: string | null;
  collection_of_light?: string | null;
  considerations_before_judgement?: string[];
}

// ============================================================================
// Abū Maʿshar corpus — chart-aware types for the Movement chapter (M1).
// Backend endpoints:
//   POST /api/library/abumashar/planets_v2/   → AbumasharPlanetsResult
//   POST /api/library/abumashar/fortitude_v2/ → AbumasharFortitudeWithLotsResult
// Schema mirrors Pydantic models in routers/library.py:251-477.
// Used to render per-period citation prose under each Mahadasha card.
// ============================================================================

export interface AbumasharCitation {
  source_ref: string | null;
  author: string | null;
  authors?: string[] | null;
  section_title?: string | null;
  book?: string | null;
  part?: string | null;
  chapter?: string | null;
  section?: string | null;
  tradition?: string | null;
  excerpt?: string | null;
  confidence?: string | null;
}

/**
 * Per-planet Abū Maʿshar verdict. Two flavors:
 *  - planets_v2 (`AbumasharPlanetVerdict`): chart-aware per-sign + per-house
 *    prose with dignity/sect/condition tier. This is the version the
 *    per-period dasha cards consume — "Mars in Cancer, fallen, in the
 *    4th house, brings X periods of family trouble, karmic restructuring."
 *  - fortitude_v2 (`AbumasharFortitudeVerdict`): chart-independent one
 *    sentence per planet from `abumashar_fortitude/fortitude.json` —
 *    doctrine only, no chart position.
 *
 * The union shape below handles both, since the frontend may need to
 * render either (Movement uses planets_v2; cited as fortitude if planet
 * is absent).
 */
export interface AbumasharPlanetVerdict {
  planet: string;
  sign?: string | null;
  house?: number | null;
  verdict_text: string;
  short?: string | null;
  advice?: string | null;
  voice_terms?: string[];
  citations: AbumasharCitation[];
  tags: string[];
  // planets_v2 only — chart-aware enrichment
  dignity?: string | null;
  dignity_score?: number | null;
  condition_tier?: number | null;
  condition_tier_name?: string | null;
  functional_nature?: string | null;
  sign_lord?: string | null;
  sign_lord_sign?: string | null;
  sign_lord_house?: number | null;
  ruled_houses?: number[];
  ruled_houses_lords?: Array<Record<string, unknown>>;
  indirect_strength?: string | null;
}

export interface AbumasharPlanetsResult {
  planets: AbumasharPlanetVerdict[];
  total_citations: number;
  schema_version: string;
}

export interface AbumasharLotVerdict {
  name: string;
  sign?: string | null;
  house?: number | null;
  lord?: string | null;
  lord_sign?: string | null;
  lord_house?: number | null;
  sect?: string | null;
  formula?: string | null;
  prose?: string | null;
}

export interface AbumasharFortitudeWithLotsResult {
  planets: AbumasharPlanetVerdict[];
  lots: AbumasharLotVerdict[];
  lots_count: number;
  total_citations: number;
  schema_version: string;
  lots_schema_version: string;
}

// ============================================================================
// Today (v2) — personalized daily feed
// ============================================================================
export interface TodayHora {
  lord: string;
  hora_index: number;
  is_day_hora: boolean;
  hora_start_utc: string;
  hora_end_utc: string;
  hora_duration_min: number;
  weekday: string;
  day_lord: string;
  sunrise_utc: string;
  sunset_utc: string;
}

export interface TodayNow {
  instant_utc: string;
  hora: TodayHora;
}

export interface TodayPersonalizedHora {
  lord: string;
  natal_house: number;
  natal_sign: string;
  natal_significations: string;
  verdict: string;
  applies_until_utc: string;
}

export interface RagaInfo {
  name: string;
  rasa: string;
  time_of_day: string;
  vadi_swara: string;
  source_text: string;
}

export interface TodayDasha {
  mahadasha: string;
  antardasha: string;
  pratyantardasha: string;
  sookshma: string | null;
  headline: string;
  mahadasha_start: string;
  mahadasha_end: string;
  md_raga: RagaInfo | null;
  ad_raga: RagaInfo | null;
  pd_raga: RagaInfo | null;
}

export interface TodayTransit {
  score: number;
  planet: string;
  transit_house: number;
  to_natal_planet: string;
  aspect: string;
  orb_deg: number;
  intensity: string;
  verdict: string;
}

export interface TodayDayContext {
  weekday: string;
  day_lord: string;
  sunrise: string;
  sunset: string;
}

export interface TodayResponse {
  now: TodayNow;
  personalized_hora: TodayPersonalizedHora;
  dasha: TodayDasha;
  transits_today: TodayTransit[];
  day_context: TodayDayContext;
}

// ============================================================================
// Classical Library — read-only primary sources
// ============================================================================
export interface LibraryBook {
  slug: string;
  title: string;
  author: string;
  era: string;
  tradition: string;
  summary: string;
}

export interface LibraryBooksResponse {
  traditions: Array<{ name: string; books: LibraryBook[] }>;
  total_books: number;
}

export interface LibraryChapterToc {
  id: string;
  title: string;
}

export interface LibraryBookDetail {
  slug: string;
  title: string;
  author: string;
  era: string;
  tradition: string;
  summary: string;
  chapter_count: number;
  table_of_contents: LibraryChapterToc[];
}

export interface LibraryChapter {
  book_slug: string;
  book_title: string;
  author: string;
  chapter_id: string;
  title: string;
  text: string;
  prev_chapter_id: string | null;
  next_chapter_id: string | null;
  char_count: number;
}

// ============================================================================
// Server-side chart caching (v2)
// ============================================================================
export interface V2ChartResponse {
  success: boolean;
  chart_id: string;
  birth: AstrologyInput;
  chart_snapshot: Record<string, unknown>;
}

export function toV2BirthData(input: AstrologyInput) {
  return {
    year: input.year,
    month: input.month,
    day: input.date,
    hour: input.hours,
    minute: input.minutes,
    second: input.seconds,
    latitude: input.latitude,
    longitude: input.longitude,
    timezone: input.timezone,
    timezone_name: (input.timezone_name ?? null) as string | null,
    place: null as string | null,
  };
}

// ============================================================================
// Transit Navigator — per-date transit positions
// ============================================================================
export interface TransitPlanet {
  transiting_planet: string;
  transit_sign: string;
  sign_number: number;
  fulldegree: number;
  is_retro: string;
  natal_house: number;
}

export interface TransitsResponse {
  query_date: string;
  today_sky: {
    moon_sign: string;
    planets: TransitPlanet[];
  };
  transits: TransitPlanet[];
  transits_by_natal_house: Record<string, number>;
  sade_sati: { active: boolean; phase: string | null; description: string };
  saturn_return: { active: boolean; delta_degrees?: number; description?: string };
  jupiter_return: { active: boolean; delta_degrees?: number; description?: string };
}

export interface TransitTimelineResponse {
  schema_version: string;
  stance: string;
  range: { start_age: number; end_age: number };
  source_refs: string[];
  years: TransitTimelineYear[];
}

export interface TransitTimelineYear {
  age: number;
  year: number;
  query_date: string;
  dasha: {
    mahadasha?: string | null;
    antardasha?: string | null;
    pratyantardasha?: string | null;
    sookshmadasha?: string | null;
    prandasha?: string | null;
  };
  profection: {
    age: number;
    house: number;
    sign?: string | null;
    lord?: string | null;
  };
  slow_transits: Record<"Saturn" | "Jupiter" | "Rahu" | "Ketu", SlowTransitPlanet>;
  inference: {
    headline: string;
    tone: string;
    support_score: number;
    pressure_score: number;
    transition_score: number;
    activated_houses: number[];
    summary: string;
    lines: string[];
    query_date: string;
  };
}

export interface SlowTransitPlanet {
  planet: string;
  sign: string;
  sign_number: number;
  degree: number;
  longitude: number;
  is_retrograde: boolean;
  natal_house: number;
  house_topic: string;
  theme: string;
}

// ============================================================================
// Chart Diagnostics — surfaces the classical-doctrine modules used by the
// predictive engine. Per-house Bhāva Bala, per-planet Sun-relationship,
// Shadbala, mutual receptions, and Hellenistic Lots.
// ============================================================================
export interface ChartDiagnosticsHouse {
  house: number;
  sign: number;
  lord: string;
  is_lagna: boolean;
  total: number;
  rating: "very_strong" | "strong" | "moderate" | "weak" | "very_weak";
  components: {
    lord_placement: number;
    reception: number;
    aspects: number;
    occupants: number;
    joy: number;
    lord_karaka_conj: number;
  };
}
export interface ChartDiagnosticsPlanet {
  sign: number;
  degree_in_sign: number;
  house: number;
  retrograde: boolean;
  is_lagna_lord: boolean;
  combustion: {
    state: "cazimi" | "combust" | "near_sun" | "free";
    separation_deg: number | null;
    mitigated: boolean;
    rationale: string;
  };
  mrityu_bhaga: boolean;
  joy_house: number | null;
  in_joy_house: boolean;
  shadbala?: {
    rupas: number;
    virupas: number;
    rating: "very_strong" | "strong" | "moderate" | "weak";
    components: {
      sthana: number; dig: number; kala: number;
      cheshta: number; naisargika: number; drik: number;
    };
  };
}
export interface ChartDiagnosticsReception {
  p1: string;
  p2: string;
  type: "domicile" | "exaltation" | "mixed";
  score: number;
}
export interface ChartDiagnosticsLot {
  longitude: number;
  sign: number;
  lord: string;
}
export interface ChartDiagnosticsResponse {
  asc_sign: number;
  lagna_lord: string;
  is_day_birth: boolean;
  houses: ChartDiagnosticsHouse[];
  planetary: Record<string, ChartDiagnosticsPlanet>;
  receptions: ChartDiagnosticsReception[];
  lots: Record<string, ChartDiagnosticsLot>;
}

// ============================================================================
// Council of Twelve — House-centric response types
// ============================================================================
export interface HouseAspect {
  from_planet: string;
  kind: string;          // "trine" | "square" | "opposition" | "conjunction" | etc.
  strength: number;      // 0.0 – 1.0
}

export interface VerdictCitation {
  name: string;             // rule name, e.g. "12 Bhavas — karakatwas"
  source_citation: string;  // e.g. "Brihat Parashara Hora Shastra Ch.11 v.2-16"
  indication: string;       // short classical line for this house (may be empty)
  rule_id?: string;
}

export interface HouseVerdict {
  persona: string;
  tradition: string;
  reading: string;
  confidence: number;    // 0 – 100
  /** Sourced classical citation backing this reading. Present only for the
   *  six personas whose master appears in the rules corpus (Parashara,
   *  Jaimini, Varahamihira, Valens, Abu Ma'shar, Bonatti). The `reading`
   *  text already includes a trailing citation, so older clients that
   *  ignore this field still render the source. Newer surfaces can render
   *  the citation as a separate chip. */
  citation?: VerdictCitation | null;
}

export interface HouseCouncilEntry {
  house: number;
  sign: string;
  theme_short: string;
  theme_detail: string;
  lord: string;
  lord_sign: string;
  lord_house: number;
  lord_dignity: string;  // "exalted" | "own sign" | "debilitated" | "neutral" | etc.
  planets: string[];
  aspects: HouseAspect[];
  strength_score: number;
  benefic_count: number;
  malefic_count: number;
  verdicts: HouseVerdict[];
  agreement_score?: number;
  agreement_total?: number;
  agreement_pct?: number;
  dissent_count?: number;
  dissenting_verdicts?: HouseVerdict[];
}

export interface CouncilResponse {
  personas_consulted: string[];
  house_council: HouseCouncilEntry[];
  synthesis: string;
  verdicts: unknown[];   // legacy — always empty in new format
}


// ============================================================================
// Composite chart — midpoint relationship wheel
// ============================================================================

// ============================================================================
// Generic "not yet implemented" guard for placeholder v2 endpoints.
// Several v2 routes (composite, davison, progressions, solar-arc,
// heliocentric, harmonic, midpoints, hyleg, cells, listen-feedback)
// return `{ unsupported: true, feature, message }` with HTTP 200 while
// the implementation is being rebuilt. Treating those as data crashes
// the UI (e.g. `compositeResult.planets.map(undefined)` in Synastry).
// Use this helper wherever a placeholder response can land.
// ============================================================================
export interface UnsupportedResponse {
  unsupported: true;
  feature?: string;
  message?: string;
}

export function isUnsupportedResponse(value: unknown): value is UnsupportedResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { unsupported?: unknown }).unsupported === true
  );
}

export function comingSoonMessage(value: unknown, fallback = "This view is not yet available."): string {
  if (isUnsupportedResponse(value)) {
    return value.message || `${value.feature || "This feature"} is not yet available. We're rebuilding it on top of the new pipeline.`;
  }
  return fallback;
}

// ============================================================================
// Compute tab — Midpoints, Progressions, Solar Arc, Harmonic, Heliocentric
// ============================================================================
export interface ComputePlanet {
  name: string;
  longitude: number;
  sign: string;
}

export interface MidpointEntry {
  a: string;
  b: string;
  midpoint_longitude: number;
  midpoint_sign: string;
  midpoint_sign_index: number;
}

export interface MidpointsResponse {
  natal_planets: { name: string; longitude: number }[];
  midpoints: MidpointEntry[];
}

export interface ProgressionsResponse {
  method: string;
  age_years: number;
  progressed_moment_utc: string;
  natal_planets: ComputePlanet[];
  progressed_planets: ComputePlanet[];
}

export interface SolarArcPlanet {
  name: string;
  natal_longitude: number;
  directed_longitude: number;
  directed_sign: string;
}

export interface SolarArcResponse {
  method: string;
  age_years: number;
  solar_arc_deg: number;
  natal_sun_longitude: number;
  progressed_sun_longitude: number;
  directed_planets: SolarArcPlanet[];
}

export interface HarmonicPlanet {
  name: string;
  natal_longitude: number;
  harmonic_longitude: number;
  harmonic_sign: string;
}

export interface HarmonicResponse {
  method: string;
  harmonic_n: number;
  natal_planets: ComputePlanet[];
  harmonic_planets: HarmonicPlanet[];
}

export interface HeliocentricResponse {
  method: string;
  planets: ComputePlanet[];
  note: string;
}

// ============================================================================
// Ephemeris — daily planetary positions
// ============================================================================
export interface EphemerisPlanet {
  name: string;
  longitude: number;
  sign: string;
  sign_index: number;
  degree_in_sign: number;
  is_retro: boolean;
  speed: number;
  house: number;
}

export interface EphemerisResponse {
  date: string;
  julian_day: number;
  planets: EphemerisPlanet[];
  ascendant: { sign: string; degree: number; longitude: number };
  moon_phase: { phase_name: string; illumination_percent: number };
}

// ============================================================================
// Panchang — daily Vedic almanac
// ============================================================================
export interface PanchangTimeWindow {
  start: string;
  end: string;
}

export interface PanchangTithi {
  name: string;
  number: number;
  paksha: string;
  end_time: string | null;
}

export interface PanchangNakshatra {
  name: string;
  lord: string;
  end_time: string | null;
}

export interface PanchangYoga {
  name: string;
  end_time: string | null;
}

export interface PanchangKarana {
  name: string;
  end_time: string | null;
}

export interface PanchangResponse {
  date: string;
  weekday: string;
  tithi: PanchangTithi;
  nakshatra: PanchangNakshatra;
  yoga: PanchangYoga;
  karana: PanchangKarana;
  sunrise: string;
  sunset: string;
  rahu_kalam: PanchangTimeWindow;
  yamaganda: PanchangTimeWindow;
  gulika: PanchangTimeWindow;
  abhijit_muhurta: PanchangTimeWindow | null;
  varjyam: string | null;
  day_lord: string;
  hora_lord: string;
}

// ============================================================================
// Relationship — deep relationship analysis (v2)
// ============================================================================
export interface PredictionResponse {
  method: string;
  year: number;
  predictions: Array<{
    area: string;
    theme: string;
    confidence: number;
    timing: string;
  }>;
  solar_return?: {
    ascendant: string;
    planets: ComputePlanet[];
    highlights: string[];
  };
  profections?: {
    profected_ascendant: string;
    lord_of_the_year: string;
    house_themes: string[];
  };
}

// ============================================================================
// Search — celestial body lookup
// ============================================================================
export interface SearchResult {
  name: string;
  type: string;
  longitude: number;
  sign: string;
  house: number;
  magnitude?: number;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
}

// ============================================================================
// Share / Export / Import
// ============================================================================
export interface ShareResponse {
  share_url: string;
  expires_at: string;
  short_code: string;
}

export interface ExportResponse {
  format: string;
  data: string;
  filename: string;
}

// ============================================================================
// Kalachakra — Tibetan astrology
// ============================================================================
export interface KalachakraResponse {
  system: string;
  cycles: Array<{
    name: string;
    start_date: string;
    end_date: string;
    lord: string;
    theme: string;
  }>;
}

// ============================================================================
// Sarvatobhadra — Vedic chakra
// ============================================================================
export interface SarvatobhadraResponse {
  nakshatra_lords: Record<string, string>;
  vedha_points: Array<{
    from: string;
    to: string;
    nature: string;
  }>;
  auspicious_directions: string[];
}

// ============================================================================
// Tajaka — annual chart
// ============================================================================
export interface TajakaResponse {
  year: number;
  ascendant: string;
  muntha: { house: number; sign: string };
  sahams: Record<string, string>;
  highlights: string[];
}

// ============================================================================
// Muntha — Jaimini annual
// ============================================================================
export interface MunthaResponse {
  year: number;
  muntha_sign: string;
  muntha_house: number;
  lord: string;
  lord_placement: string;
  predictions: string[];
}

// ============================================================================
// Prashna — horary (v2)
// ============================================================================
export interface PrashnaResponse {
  question: string;
  chart_id: string;
  significator: string;
  answer: string;
  confidence: number;
  timing: string | null;
}

// ============================================================================
// Numerology / Tarot
// ============================================================================
export interface NumerologyResponse {
  life_path: number;
  destiny: number;
  soul_urge: number;
  personality: number;
  interpretation: string;
}

export interface TarotResponse {
  spread: string;
  cards: Array<{
    name: string;
    position: string;
    meaning: string;
    reversed: boolean;
  }>;
  overall_theme: string;
}

// ============================================================================
// Ayanamsa / House-system / Orb
// ============================================================================
export interface AyanamsaResponse {
  date: string;
  system: string;
  ayanamsa_value: number;
  lahiri_offset: number;
  true_chitrapaksha_offset: number;
  raman_offset: number;
  kp_offset: number;
  fagan_bradley_offset: number;
  tropical_offset: number;
  supported_systems: string[];
}

export interface HouseSystemResponse {
  systems: Record<string, Array<{ house: number; cusp: number; sign: string }>>;
}

export interface OrbResponse {
  default_orbs: Record<string, number>;
  custom_orbs?: Record<string, number>;
}

export interface CompareResponse {
  ashtakuta?: Record<string, unknown>;
  mangal_dosha?: Record<string, unknown>;
  cross_placements?: unknown[];
}

export interface SimilarCelebrity {
  id: string;
  name: string;
  similarity: number;
  matches: string[];
  rodden: string;
  born: string;
  events: Array<{ date: string; event: string; category: string }>;
  birth_year: number;
  ascendant: string;
  moon_sign: string;
  sun_sign: string;
  yogas: string[];
}

export interface SimilarCelebritiesResponse {
  success: boolean;
  user_chart_id: string;
  user_features: {
    ascendant: string;
    moon_sign: string;
    sun_sign: string;
    yogas: string[];
  };
  similar_celebrities: SimilarCelebrity[];
}


export interface CalculateResponse {
  success: boolean;
  chart_id: string;
  params?: Record<string, unknown>;
  report: Record<string, unknown>;
}

export const api = {
  /**
   * Pre-warms all 6 Watch chapters asynchronously in parallel upon chart casting
   * or selection, so offline navigation across Reading, Movement, Council,
   * Journey, Sound, and Sky is instantaneous and resilient to offline mode.
   */
  prefetchWatchData: async (input: AstrologyInput): Promise<void> => {
    const birth = toV2BirthData(input);
    const body = {
      birth,
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    };
    await Promise.allSettled([
      postChart("/api/v2/chart", body),
      postChart("/api/v2/timeline", body),
      postChart("/api/v2/council", input),
      postChart("/api/v2/transit-timeline", body),
      postChart("/api/v2/harmonics", body),
      postChart("/api/v2/life-spiral", body),
      postChart("/api/v2/timeline/analogs", body),
      postChart("/api/v2/celebrities/similar", body),
    ]);
  },

  report: (input: AstrologyInput) => post<Report>("/report/", input),
  reportMarkdown: (input: AstrologyInput) => post<string>("/report/markdown/", input, "text"),
  reportNarrative: (input: AstrologyInput) => post<string>("/report/narrative/", input, "text"),
  reportPdf: (input: AstrologyInput) => postBlob("/report/pdf/", input),
  transits: (input: AstrologyInput, onDate?: string) =>
    post<TransitsResponse>(`/transits/${onDate ? `?on_date=${onDate}` : ""}`, input),
  eventTiming: (chart: AstrologyInput, event_type: string) =>
    post<unknown>("/event-timing/", { chart, event_type }),

  /** Horary topic catalogue for Ask suggestion chips. */
  horaryTopics: () =>
    get<{ topics: string[] }>("/api/v2/horary/topics"),

  /**
   * Bonatti horary verdict. `askedAt` is the moment of asking (now + location).
   * Response includes matched_topic, verdict, rationale, significators.
   */
  horary: (question: string, askedAt: AstrologyInput) =>
    postChart<HoraryResponse>("/api/v2/horary", {
      question,
      asked_at: toV2BirthData(askedAt),
      ayanamsha: askedAt.ayanamsha ?? "lahiri",
    }),
  journey: (input: AstrologyInput, maxItemsPerBand?: number) =>
    postChart<JourneyResponse>("/api/v2/journey", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      max_items_per_band: maxItemsPerBand ?? null,
    }),
  timeline: (input: AstrologyInput) =>
    postChart<TimelineResponse>("/api/v2/timeline", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  transitTimeline: (input: AstrologyInput, startAge = 0, endAge = 90) =>
    postChart<TransitTimelineResponse>("/api/v2/transit-timeline", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      start_age: startAge,
      end_age: endAge,
    }),
  /**
   * Full life-event windows. Rate-limited 10/min/IP — callers MUST use
   * `getLifePredictions` cache (one call per chart per session).
   */
  lifePredictions: (input: AstrologyInput, topNPerEvent = 5) =>
    postChart<LifePredictionsResponse>("/api/v2/life-predictions", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      top_n_per_event: topNPerEvent,
    }),
  /**
   * Non-clinical health-state ledger (steady|sensitive|turning|strong).
   * May 404 until the backend route is deployed — callers must fallback.
   */
  healthTimeline: (input: AstrologyInput) =>
    postChart<HealthTimelineResponse>("/api/v2/health-timeline", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  timelineMonthly: (input: AstrologyInput, year: number) =>
    postChart<unknown>("/api/v2/timeline/monthly", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      year,
    }),

  // ── Abū Maʿshar corpus, chart-aware variants ─────────────────────────
  // Each carries the chart-aware Mahadasha-relevant prose used by the
  // Movement chapter's per-period cards (M1). Backed by:
  //   - /api/library/abumashar/planets_v2/   → per-planet (sign + house) prose
  //   - /api/library/abumashar/fortitude_v2/ → 9-planet corpus + 7 Arabic Lots
  // Rate-limited at 10/minute on the backend (heavy compute tier).
  abumasharPlanetsV2: (input: AstrologyInput) =>
    postChart<AbumasharPlanetsResult>("/api/library/abumashar/planets_v2/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  abumasharFortitudeV2: (input: AstrologyInput) =>
    postChart<AbumasharFortitudeWithLotsResult>(
      "/api/library/abumashar/fortitude_v2/",
      {
        birth: toV2BirthData(input),
        ayanamsha: input.ayanamsha,
        observation_point: input.observation_point,
      },
    ),
  /**
   * Daily feed for a chart. Optional `at` (ISO timestamp) freezes the
   * "now" instant — used by the Today tab for "Yesterday, verified".
   */
  today: (input: AstrologyInput, opts?: { at?: string }) =>
    postChart<TodayResponse>("/api/v2/today", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      ...(opts?.at ? { at: opts.at } : {}),
    }),
  /**
   * Planner: score one chosen instant for the natal chart (planner.v1).
   * `at` is an ISO8601 UTC instant; `durationMin` is the plan length
   * (default 45, cap 240). Returns score/band/headline, the full transit
   * aspect set, wheel positions, dimensions, hora, dasha and the
   * composed recommendation.
   */
  plannerWindow: (input: AstrologyInput, at: string, durationMin?: number) =>
    postChart<PlannerWindowResponse>("/api/v2/planner/window", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      at,
      ...(durationMin ? { duration_min: durationMin } : {}),
    }),
  /**
   * Planner: scan a UTC range (≤72h) at `granularityMin` (5-60) and get
   * a score series plus the top ≤5 moments — powers Best Times and the
   * live scrubber. `durationMin` applies per candidate instant.
   */
  plannerScan: (
    input: AstrologyInput,
    startUtc: string,
    endUtc: string,
    granularityMin?: number,
    durationMin?: number,
  ) =>
    postChart<PlannerScanResponse>("/api/v2/planner/scan", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      start_utc: startUtc,
      end_utc: endUtc,
      ...(granularityMin ? { granularity_min: granularityMin } : {}),
      ...(durationMin ? { duration_min: durationMin } : {}),
    }),
  /**
   * Planner: score a bounded grid (≤30d) at `granularityMin` (30-240) and
   * get per-day scored series plus each day's best/top instants — powers
   * the week view. `durationMin` spaces the per-day top picks.
   */
  plannerHorizon: (
    input: AstrologyInput,
    startUtc: string,
    endUtc: string,
    granularityMin?: number,
    durationMin?: number,
  ) =>
    postChart<PlannerHorizonResponse>("/api/v2/planner/horizon", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      start_utc: startUtc,
      end_utc: endUtc,
      ...(granularityMin ? { granularity_min: granularityMin } : {}),
      ...(durationMin ? { duration_min: durationMin } : {}),
    }),
  saveChartV2: (input: AstrologyInput) =>
    postChart<V2ChartResponse>("/api/v2/chart", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  similarCelebrities: (input: AstrologyInput) =>
    postChart<SimilarCelebritiesResponse>("/api/v2/celebrities/similar", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  getChartById: (chartId: string) => get<V2ChartResponse>(`/api/v2/chart/${chartId}`),
  ascendantProfile: (sign: string) =>
    get<AscendantProfile>(`/api/ascendant-profiles/${encodeURIComponent(sign.toLowerCase())}/`),

  chartDiagnostics: (chart: AstrologyInput) =>
    postChart<ChartDiagnosticsResponse>("/chart-diagnostics/", { chart }),
  council: (input: AstrologyInput) => post<CouncilResponse>("/council/", input),

  // ── Face-Table verdict endpoints (Watch redesign §3 — Face sections) ───
  // Mirrors iOS FaceVerdictAPIClient and Android FaceTableApi. These are the
  // inline-cited readings the Face chapter renders, one row per (house |
  // aspect | planet | lot). All four are POST /api/v2/library/* shims that
  // forward to the legacy /api/library/* handlers (see routers/v2_library.py
  // — the shim exists so the v2-prefixed client only knows one base URL).
  //
  // Schema is namespaced in src/api/faceTable.ts to avoid colliding with
  // the council chapter's HouseVerdict (src/api/client.ts:531-560), which
  // has a different shape (persona + tradition + reading per (place, persona)
  // pair vs the face-table topic + verdict_text + voice_terms per place).
  faceHouses: (input: AstrologyInput) =>
    postChart<FaceHouseAssessmentResult>("/api/v2/library/abumashar/houses/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  faceHouseOccupants: (input: AstrologyInput) =>
    postChart<FaceHouseAssessmentResult>("/api/v2/library/abumashar/houses_occupant/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  faceAspects: (input: AstrologyInput) =>
    postChart<FaceAspectAssessmentResult>("/api/v2/library/abumashar/aspects/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  faceFortitudeV2: (input: AstrologyInput) =>
    postChart<FortitudeWithLotsResult>("/api/v2/library/abumashar/fortitude_v2/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  // Face Table 7 — what each planet means for THIS native: seven strands
  // (sign, strength, ascendant, house, joy, reception, aspects) inferred
  // separately, plus a synthesis. Unlike faceHouseOccupants, which returns
  // the same house corpus for every chart, this is computed from the
  // querent's own positions.
  facePlanetReading: (input: AstrologyInput) =>
    postChart<PlanetReadingResult>("/api/v2/library/abumashar/planet_reading/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  facePlanets: (input: AstrologyInput) =>
    postChart<PlanetAssessmentResult>("/api/v2/library/assessment/planets/", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),

  // ── R-311 Planetary Status · the engine behind the Hero App ──────────
  // Per-planet deep analysis: dignity, sect, combustion/CAZIMI, graha drishti,
  // parivartana, houses ruled, functional nature + verbatim classical citations.
  // If `planet` is omitted, returns all 9 navagraha.
  planetaryStatus: (input: AstrologyInput, planet?: string) =>
    postChart<unknown>("/api/v2/chart/planetary-status/", {
      chart: {
        year: input.year, month: input.month, date: input.date,
        hours: input.hours, minutes: input.minutes, seconds: input.seconds,
        latitude: input.latitude, longitude: input.longitude, timezone: input.timezone,
        observation_point: input.observation_point, ayanamsha: input.ayanamsha,
      },
      planet,
    }),

  // Year-by-year dasha + transit overlay timeline.
  statusTimeline: (input: AstrologyInput, end_year?: number) =>
    postChart<unknown>("/api/v2/chart/status-timeline/", {
      chart: {
        year: input.year, month: input.month, date: input.date,
        hours: input.hours, minutes: input.minutes, seconds: input.seconds,
        latitude: input.latitude, longitude: input.longitude, timezone: input.timezone,
        observation_point: input.observation_point, ayanamsha: input.ayanamsha,
      },
      end_year,
    }),

  // ── S10 corpus transparency · frozen Laya topic map (Sprint Zero) ─
  rulesTopics: () => get<unknown>("/api/v2/rules/topics"),

  // ── R-318 Dasha Analogs · historical parallels for your current period ─
  // "You are now where Steve Jobs was when he founded Apple (Ketu/Ketu)."
  dashaAnalogs: (input: AstrologyInput) =>
    postChart<unknown>("/api/v2/timeline/analogs", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  midpoints: (input: AstrologyInput) =>
    postChart<MidpointsResponse>("/api/v2/compute/midpoints", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  progressions: (input: AstrologyInput, ageYears: number) =>
    postChart<ProgressionsResponse>("/api/v2/compute/progressions", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      age_years: ageYears,
    }),
  solarArc: (input: AstrologyInput, ageYears: number) =>
    postChart<SolarArcResponse>("/api/v2/compute/solar-arc", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      age_years: ageYears,
    }),
  harmonic: (input: AstrologyInput, harmonicNumber: number) =>
    postChart<HarmonicResponse>("/api/v2/compute/harmonic", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
      harmonic_number: harmonicNumber,
    }),
  heliocentric: (input: AstrologyInput) =>
    postChart<HeliocentricResponse>("/api/v2/compute/heliocentric", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  houses: (input: AstrologyInput) =>
    postChart<{
      houses: HouseSection[];
      house_strength: Record<string, unknown>;
      house_lords_strength: Record<string, unknown>;
      ascendant: { sign: string; degree: number; lord: string };
    }>("/api/v2/houses", {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    }),
  ariesIngress: (year: number, latitude: number, longitude: number, timezone: number) =>
    postMundane<unknown>("/mundane/aries-ingress/", { year, latitude, longitude, timezone }),
  greatConjunction: (input: AstrologyInput) =>
    postMundane<unknown>("/mundane/great-conjunction/", input),

  // Mundane chapter (VII) — session-2026-07-01. Present-moment forecast
  // + 100-year historical analog map. See MundaneResponse /
  // MundaneTimelineResponse in types.ts.
  mundane: (year: number, month?: number) =>
    getMundane<MundaneResponse>(`/api/v2/mundane?year=${year}${month != null ? `&month=${month}` : ""}`),
  mundaneTimeline: (from: number = 1926, to: number = 2026, maxEvents?: number) => {
    const params = new URLSearchParams();
    params.set("from_year", String(from));
    params.set("to_year", String(to));
    if (maxEvents != null) params.set("max_events", String(maxEvents));
    return getMundane<MundaneTimelineResponse>(`/api/v2/mundane/timeline?${params.toString()}`);
  },
  // mundane_v research bundle (ports #2/#4): Mundane Discoveries + the
  // 101-year monthly century series.
  mundaneDiscoveries: () =>
    getMundane<MundaneDiscoveriesResponse>("/api/v4_13/mundane-discoveries"),
  mundaneCenturySeries: () =>
    getMundane<MundaneCenturySeries>("/api/v4_13/mundane-century-series"),
  mundaneOnThisDay: (mmdd: string, limit = 8) =>
    getMundane<MundaneOnThisDayResponse>(
      `/api/v4_13/mundane-on-this-day?mmdd=${encodeURIComponent(mmdd)}&limit=${limit}`,
    ),
  mundanePreRegStatus: () =>
    getMundane<MundanePreRegStatusResponse>("/api/v4_13/mundane-pre-reg-status"),
  // Exclusive Chronos briefing (evidence layer): year signal w/ hit rates,
  // regions, classes, weather lots, historical echo, falsifications, and the
  // 2026 live YTD verdict — the product grading itself in real time.
  mundaneBriefing: (year: number) =>
    getMundane<MundaneBriefingResponse>(`/api/v4_13/mundane-briefing?year=${year}`),
  mundaneYtdVerdict: () =>
    getMundane<MundaneYtdVerdictResponse>("/api/v4_13/mundane-ytd-verdict"),
  // Chronos rule evidence: the year-level classical signal with per-rule
  // reasons (rule → source → n → hit rate → caveat) + the contributing
  // validated rule catalog the risk radar scores with.
  mundaneChronosYear: (year: number) =>
    getMundane<MundaneChronosYearResponse>(`/api/v4_13/mundane-chronos-year?year=${year}`),
  mundaneChronosRules: () =>
    getMundane<MundaneChronosRuleCatalogResponse>("/api/v4_13/mundane-chronos-rules"),
  // Mundane Weather (iOS parity): daily world-line score, score reasoning
  // (frozen-2026 window only) and the countries heat panel.
  mundaneToday: (date?: string) =>
    getMundane<MundaneTodayResponse>(`/api/v2/mundane/today${date ? `?date=${date}` : ""}`),
  mundaneExplain: (date: string) =>
    getMundane<MundaneExplainResponse>(`/api/v2/mundane/explain?date=${date}`),
  mundaneCountriesToday: (date?: string) =>
    getMundane<MundaneCountriesTodayResponse>(`/api/v2/mundane/countries/today${date ? `?date=${date}` : ""}`),
  mundaneOutcomesSummary: () =>
    getMundane<MundaneOutcomesSummaryResponse>("/api/v2/mundane/outcomes/summary"),
  // P3 deep-tier country profiles: authored static pack. The index query is
  // fetched with a 24h staleTime and its failure is swallowed silently (no
  // badges); a profile 404 simply means "not a deep-tier country".
  mundaneProfiles: () =>
    getMundane<MundaneProfilesIndexResponse>("/api/v2/mundane/profiles"),
  mundaneCountryProfile: (code: string) =>
    getMundane<MundaneCountryProfileResponse>(`/api/v2/mundane/country/${code}/profile`),
  eclipsesNatal: (input: AstrologyInput) =>
    post<unknown>("/eclipses/natal/", input),
  termDirections: (input: AstrologyInput) =>
    post<unknown>("/term-directions/", input),
  solarReturn: (input: AstrologyInput) =>
    post<unknown>("/solar-return/", input),
  firdaria: (input: AstrologyInput) =>
    post<unknown>("/firdaria/", input),
  harmonics: (input: AstrologyInput, tradition?: "carnatic" | "western") => {
    const path = tradition
      ? `/api/v2/harmonics?tradition=${encodeURIComponent(tradition)}`
      : "/api/v2/harmonics";
    return postChart<HarmonicsResponse>(path, {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    });
  },

  /** Chart → signature + sectionPlan + suggestedRenderBody (needs Node render service). */
  harmonicsPlan: (input: AstrologyInput, tradition?: "carnatic" | "western") => {
    const path = tradition
      ? `/api/v2/harmonics/plan?tradition=${encodeURIComponent(tradition)}`
      : "/api/v2/harmonics/plan";
    return postChart<{
      success: boolean;
      signature: HarmonicsResponse["signature"];
      recommend?: Record<string, unknown>;
      sectionPlan?: Record<string, unknown>;
      suggestedRenderBody?: Record<string, unknown>;
    }>(path, {
      birth: toV2BirthData(input),
      ayanamsha: input.ayanamsha,
      observation_point: input.observation_point,
    });
  },

  celebrityDashaTimeline: (celebrityId: string) =>
    get<{
      celebrity: { id: string; name: string; born: string; confidence: string; events: unknown[] };
      birth_mahadasha?: string;
      dasha_timeline: Array<Record<string, unknown>>;
    }>(`/api/v2/celebrities/${encodeURIComponent(celebrityId)}/dasha-timeline`),
  celebrityAssessment: (celebrityId: string) =>
    get<CelebrityAssessmentResponse>(
      `/api/v2/celebrities/${encodeURIComponent(celebrityId)}/assessment`,
    ),
  celebrityCatalog: () =>
    get<{
      success: boolean;
      count: number;
      celebrities: Array<{
        id: string;
        name: string;
        born: string;
        confidence: string;
        source?: string | null;
        event_count: number;
        assessment_path: string;
      }>;
    }>("/api/v2/celebrities"),
  benchmark: () =>
    get<{success: boolean; results: Record<string, unknown>}>("/api/v2/benchmark"),
  listBooks: (tradition?: string) =>
    get<LibraryBooksResponse>("/api/v2/library/books" + (tradition ? `?tradition=${encodeURIComponent(tradition)}` : "")),
  getBook: (slug: string) =>
    get<LibraryBookDetail>(`/api/v2/library/book/${slug}`),
  getChapter: (slug: string, chapterId: string) =>
    get<LibraryChapter>(`/api/v2/library/book/${slug}/chapter/${chapterId}`),
  ephemeris: (date: string, latitude: number, longitude: number, timezone: number) =>
    post<EphemerisResponse>("/api/v2/ephemeris", { date, latitude, longitude, timezone }),
  panchang: (date: string, latitude: number, longitude: number, timezone: number) =>
    post<PanchangResponse>("/api/v2/panchang", { date, latitude, longitude, timezone }),
  // --- v2 core (remaining) ---
  compareV2: (a: AstrologyInput, b: AstrologyInput) =>
    post<CompareResponse>("/api/v2/compare", { birth_a: toV2BirthData(a), birth_b: toV2BirthData(b), ayanamsha: a.ayanamsha }),
  prediction: (input: AstrologyInput, ageYears?: number) =>
    post<PredictionResponse>("/api/v2/prediction", { birth: toV2BirthData(input), ayanamsha: input.ayanamsha, observation_point: input.observation_point, age_years: ageYears ?? null }),
  search: (query: string) =>
    post<SearchResponse>("/api/v2/search", { query }),
  calculate: (input: AstrologyInput, params: Record<string, unknown>) =>
    post<CalculateResponse>("/api/v2/calculate", { birth: toV2BirthData(input), ...params }),
  validate: (input: AstrologyInput) =>
    post<{ valid: boolean; errors: string[] }>("/api/v2/validate", { birth: toV2BirthData(input) }),
  share: (input: AstrologyInput, expiresInHours?: number) =>
    post<ShareResponse>("/api/v2/share", { birth: toV2BirthData(input), expires_in_hours: expiresInHours ?? 168 }),
  exportV2: (input: AstrologyInput, format: "json" | "csv" | "xml") =>
    post<ExportResponse>("/api/v2/export", { birth: toV2BirthData(input), format }),
  importV2: (data: string, format: "json" | "csv" | "xml") =>
    post<AstrologyInput>("/api/v2/import", { data, format }),
  printV2: (input: AstrologyInput) =>
    postBlob("/api/v2/print", { birth: toV2BirthData(input) }),
  email: (input: AstrologyInput, email: string, format?: string) =>
    post<{ sent: boolean }>("/api/v2/email", { birth: toV2BirthData(input), email, format: format ?? "pdf" }),
  subscribe: (email: string, topics: string[]) =>
    post<{ subscribed: boolean }>("/api/v2/subscribe", { email, topics }),
  feedback: (message: string, category?: string) =>
    post<{ received: boolean }>("/api/v2/feedback", { message, category: category ?? "general" }),
  // --- v2 specialized (remaining) ---
  kalachakra: (input: AstrologyInput) =>
    post<KalachakraResponse>("/api/v2/kalachakra", { birth: toV2BirthData(input), ayanamsha: input.ayanamsha }),
  sarvatobhadra: (input: AstrologyInput) =>
    post<SarvatobhadraResponse>("/api/v2/sarvatobhadra", { birth: toV2BirthData(input), ayanamsha: input.ayanamsha }),
  tajaka: (input: AstrologyInput, year?: number) =>
    post<TajakaResponse>("/api/v2/tajaka", { birth: toV2BirthData(input), ayanamsha: input.ayanamsha, year: year ?? null }),
  muntha: (input: AstrologyInput, year?: number) =>
    post<MunthaResponse>("/api/v2/muntha", { birth: toV2BirthData(input), ayanamsha: input.ayanamsha, year: year ?? null }),
  ayanamsa: (date: string, system = "lahiri") =>
    post<AyanamsaResponse>("/api/v2/ayanamsa", { date, system }),
  houseSystem: (input: AstrologyInput) =>
    post<HouseSystemResponse>("/api/v2/house-system", { birth: toV2BirthData(input), ayanamsha: input.ayanamsha }),
  orb: (customOrbs?: Record<string, number>) =>
    post<OrbResponse>("/api/v2/orb", { custom_orbs: customOrbs ?? null }),
  // --- v2 utilities (out-of-scope but typed) ---
  numerology: (birthDate: string) =>
    post<NumerologyResponse>("/api/v2/numerology", { birth_date: birthDate }),
  tarot: (question: string, spread?: string) =>
    post<TarotResponse>("/api/v2/tarot", { question, spread: spread ?? "three_card" }),

  // ── V4.13 cross-celeb feature layer (Watch redesign §6) ──────────────
  // Unlocks the 0%-utilized research layer: 7 Big Discoveries, Power Player,
  // 8th-house, Different Path, top-3 professions, lift stats, Recovery beat.
  // The backend accepts both public kebab-case and research underscore IDs.
  v4_13Features: (chartId: string) =>
    get<V413Features>(`/api/v4_13/features/${encodeURIComponent(chartId)}`),
  v4_13Celebs: () =>
    get<{ chart_ids: string[]; count: number }>(`/api/v4_13/celebs`),
  v4_13Stats: () =>
    get<{ corpus_size: number; generated_at: string | null; profession_counts: Record<string, number> }>(`/api/v4_13/stats`),
  v4_13Discoveries: () =>
    get<Array<{ title: string; finding: string; implication: string }>>(`/api/v4_13/discoveries`),

  // P1-3: canonical 7 Big Discoveries + Extended Findings beyond house lords.
  v4_13DiscoveriesExtendedV2: () =>
    get<{
      evidence_contract_version: string;
      big_discoveries: Array<DiscoveryExtended>;
      extended_findings: Array<DiscoveryExtended>;
    }>(`/api/v4_13/discoveries/extended-v2`).then((payload) => {
      if (payload.evidence_contract_version !== DISCOVERIES_EVIDENCE_CONTRACT_VERSION) {
        throw new ApiError(0, "The published Discoveries evidence receipt is out of date.");
      }
      return payload;
    }),

  // Full SYNTHESIS_REPORT.md as JSON — the cross-celeb research essay.
  // Backend wraps the markdown in {markdown, word_count, section_count,
  // source_path} so the UI can decide whether to render inline or just
  // link. Closes the documented-but-404 endpoint from MEMORY §5.
  v4_13Synthesis: () =>
    get<{
      markdown: string;
      word_count: number;
      section_count: number;
      source_path: string;
    }>(`/api/v4_13/synthesis`),

  // Daily featured chart: birthday match > transit hook > rotating curated list.
  v4_13FeaturedChart: () =>
    get<{
      chart_id: string;
      name: string;
      born: string;
      lagna: string | null;
      profession: string | null;
      hook: string;
      reason: "birthday" | "transit" | "rotation";
    }>(`/api/v4_13/featured-chart`),

  // ── Movement chapter — anonymous ✓/◐/✗ validation aggregate (spec §3) ─
  submitValidation: (body: {
    chart_id: string;
    period_key: string;
    period_lord: string;
    verdict: "right" | "partial" | "wrong";
  }) => post<{ total: number; right: number; partial: number; wrong: number; accuracy: number }>(
    "/api/validation/",
    body,
  ),
  validationAggregate: () =>
    get<{ total: number; right: number; partial: number; wrong: number; accuracy: number }>(
      "/api/validation/aggregate",
    ),
  /**
   * Planner attestation (planner v1.1): anonymous completion + verdict for a
   * past plan. Plans are local-only and carry no chart_id. Post is
   * fire-and-forget — callers `.catch(() => null)` exactly like
   * ValidationBar's submitValidation.
   */
  plannerValidation: (body: {
    completion: "done" | "late" | "skipped";
    verdict: "right" | "partial" | "wrong";
  }) => post<PlannerValidationResponse>("/api/validation/planner", body),

  // ── Interpretation Library (12-author cited corpus) ──────────────────
  // Ptolemy's drawer + the Council roster source from this. The corpus was
  // reachable only through the report blob; these endpoints surface it
  // directly. Read-only. See wiring-dark-assets spec §2.
  libraryAuthors: () =>
    get<LibraryAuthor[]>("/api/library/authors"),
  libraryCategories: () =>
    get<LibraryCategoryCount[]>("/api/library/categories"),
  libraryStats: () =>
    get<LibraryStats>("/api/library/stats"),
  libraryInterpretation: (params: {
    author?: string;
    category?: string;
    planet?: string;
    sign?: string;
    house?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.author) qs.set("author", params.author);
    if (params.category) qs.set("category", params.category);
    if (params.planet) qs.set("planet", params.planet);
    if (params.sign) qs.set("sign", params.sign);
    if (params.house !== undefined) qs.set("house", String(params.house));
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return get<LibraryInterpretation[]>(
      `/api/library/interpretation${query ? `?${query}` : ""}`,
    );
  },
  // The one call the Ptolemy drawer makes: match THIS chart across all 12
  // authors. Reuses the AstrologyInput shape, same as /report/.
  libraryMatch: (input: AstrologyInput) =>
    post<LibraryChartMatch>("/api/library/match", input),

  // ── Hero cases (18 pre-computed celebrity deep-dives) ─────────────────
  // The Archive's "display case of timepieces". Read-only static content.
  // See wiring-dark-assets spec §3 Phase C.
  heroCases: () =>
    get<HeroCaseSummary[]>("/api/cases"),
  heroCase: (slug: string) =>
    get<HeroCaseDetail>(`/api/cases/${encodeURIComponent(slug)}`),

  // ── Life spiral — unified multi-system dasha view (Phase E) ──────────
  // The 'whole-life sweep' complementing the MOVEMENT detailed timeline.
  // Reuses the canonical compute; rate-limited. See wiring spec §3 Phase E.
  lifeSpiral: (input: AstrologyInput) =>
    post<LifeSpiral>("/api/v2/life-spiral", input),
};

export { ApiError };

// Re-export the library types so pages import them from here, the same way
// they import CouncilResponse etc. (defined above).
export type {
  LibraryAuthor,
  LibraryAuthorRef,
  LibraryInterpretation,
  LibraryChartMatch,
  LibraryAuthorMatch,
  LibraryCategoryCount,
  LibraryStats,
  HeroCaseSummary,
  HeroCaseDetail,
} from "./types";
